/**
 * The gag catalog for the notification-detail view. Selected client-side
 * when a detail opens — never sent to or generated by the server, and not
 * tied to any particular notification (V1 keeps this generic; the shape
 * leaves room for per-notification gags later without a rewrite).
 */
import type { Locale } from './i18n';

export interface Gag {
  en: string;
  es: string;
}

export const GAGS: readonly Gag[] = [
  { en: 'You clicked it. That was a choice.', es: 'Le hiciste clic. Fue una decisión tuya.' },
  { en: 'This information was not worth opening.', es: 'Esta información no ameritaba abrirse.' },
  {
    en: 'Thank you for your continued attention to completely unnecessary information.',
    es: 'Gracias por tu constante atención a información completamente innecesaria.',
  },
  { en: 'Nothing else will happen.', es: 'No va a pasar nada más.' },
  { en: 'You may now return to your regularly scheduled life.', es: 'Ya podés volver a tu vida habitual.' },
  { en: 'We are pleased to confirm that you have read this.', es: 'Confirmamos, con gusto, que leíste esto.' },
  { en: 'This was the notification equivalent of opening the fridge again.', es: 'Esto fue el equivalente a abrir la heladera de nuevo.' },
  { en: 'Opening it did not make it more important.', es: 'Abrirla no la hizo más importante.' },
  { en: 'Your curiosity has been noted and ignored.', es: 'Tu curiosidad quedó registrada e ignorada.' },
  { en: "It's the same thing, just bigger.", es: 'Es lo mismo, pero más grande.' },
  { en: 'A tap well spent.', es: 'Un toque bien invertido.' },
  { en: 'This concludes the notification.', es: 'Acá termina la notificación.' },
];

const LAST_GAG_KEY = 'un_last_gag';

function getLastGagIndex(): number | null {
  try {
    const raw = sessionStorage.getItem(LAST_GAG_KEY);
    if (raw === null) return null;
    const n = Number(raw);
    return Number.isInteger(n) && n >= 0 && n < GAGS.length ? n : null;
  } catch {
    return null;
  }
}

function setLastGagIndex(index: number): void {
  try {
    sessionStorage.setItem(LAST_GAG_KEY, String(index));
  } catch {
    /* Best-effort; falling back to always-random repeats is fine. */
  }
}

/** Picks a random gag index, avoiding an immediate repeat of `previous` when there's more than one gag. */
export function pickGag(random: () => number = Math.random, previous: number | null = null): number {
  if (GAGS.length <= 1) return 0;
  let index = Math.floor(random() * GAGS.length);
  if (index === previous) index = (index + 1) % GAGS.length;
  return index;
}

/** Picks a gag, remembering it for this tab so a re-render (e.g. a language switch) doesn't silently reroll it. */
export function pickSessionGag(random: () => number = Math.random): number {
  const previous = getLastGagIndex();
  const index = pickGag(random, previous);
  setLastGagIndex(index);
  return index;
}

export function gagText(index: number, locale: Locale): string {
  // GAGS is a fixed non-empty catalog we control, so GAGS[0] is always
  // present — noUncheckedIndexedAccess just can't see that statically.
  const gag = GAGS[index] ?? GAGS[0]!;
  return locale === 'es' ? gag.es : gag.en;
}
